/**
 * J & N Hold Handling (issue #669 — "Fraud and AML checks", @dylan219).
 *
 * A 'J' hold ("Multiple Warnings") and an 'N' hold ("Pay No Withdrawal") that
 * exist SIMULTANEOUSLY on the same account are a fraud/AML stop condition: the
 * agent cannot proceed and must transfer the customer to the fraud team. The
 * system must surface, for the agent:
 *   - both hold type(s) on the affected account(s);
 *   - a clear error message that the agent cannot proceed; and
 *   - a pop-up with the exact transfer scripting, interpolating the customer's
 *     name.
 *
 * INTERPRETATION (flagged on the issue for @dylan219 to confirm): the spec
 * header says the trigger is a 'J' AND an 'N' hold "simultaneously"; a later
 * bullet says "any of these holds". We implement the header's simultaneous
 * reading — BOTH J and N present on the SAME account — as the trigger.
 *
 * This module is deliberately framework-free and side-effect-free (mirrors
 * `banking/auth-hold.ts`): it takes the already-fetched account/hold data and
 * returns the pop-up payload, so it is exhaustively unit-testable. The J/N
 * codes are read from the account holds already loaded on the Dashboard — no
 * extra gateway call and no contract change (the holds ride in the accounts
 * projection's `Holds[].Code`).
 *
 * Distinct from the CCF-43 Customer Authentication Hold Check
 * (`banking/auth-hold.ts` — the H/I/O AML + Q/8/V fraud access holds); J and N
 * are NOT auth-hold codes and this check is its own trigger.
 *
 * #784 (Option B, @dylan219 2026-09-12 "go with option B please") BROADENS the
 * trigger: a single Q/8/V FRAUD hold now also raises this same un-dismissible
 * fraud-team-transfer warning (see {@link fraudTransferCodes}) — Q/V/8 behaves
 * exactly like J&N, superseding CCF-43's earlier dismissible "log the customer
 * in, no transactions" fraud pop-up. So this module is no longer strictly "J and
 * N only"; it is the fraud-team-transfer HARD STOP for BOTH triggers.
 */

/** The two hold codes whose SIMULTANEOUS presence triggers the check. */
export const JN_HOLD_CODES = ['J', 'N'] as const;

/**
 * Fraud access holds (Q/8/V). @dylan219 #784 (Option B, 2026-09-12) ruled these
 * behave EXACTLY like a simultaneous J+N hold — an un-dismissible
 * fraud-team-transfer HARD STOP — SUPERSEDING CCF-43's earlier "fraud hold = log
 * the customer in, no transactions" treatment. So the transfer warning fires for
 * a J+N account OR any single Q/8/V fraud hold. Mirrors the server's
 * `jn-hold-block.ts` `FRAUD_HOLD_CODES`.
 */
export const FRAUD_HOLD_CODES = ['Q', '8', 'V'] as const;

/**
 * The ordinary AML access-hold codes (CCF-43). NOT fraud-team-transfer
 * TRIGGERS on their own (a H/I/O-only customer gets the dismissible
 * Acknowledge pop-up, @dylan219 #784), but issue #806 (@dylan219 2026-09-16:
 * "H,I,O hold information was not displayed") rules they must still be
 * DISPLAYED — alongside the fraud accounts — inside the fraud-team-transfer
 * warning once it is raised for a sibling account of the same customer. The
 * TRIGGER is unchanged (fraud-only); only the display set widens. Mirrors the
 * server twin in `server/src/jn-hold-block.ts`.
 */
export const ORDINARY_AML_HOLD_CODES = ['H', 'I', 'O'] as const;

/** One account as seen by the J&N check: its number and the hold codes on it. */
export interface JnHoldAccountInput {
  readonly accountNumber: string;
  /** Hold codes present on the account (any codes; non-J/N codes ignored). */
  readonly holdCodes: readonly string[];
}

/** Per-account result carried in the pop-up: last 4 + the J/N codes found. */
export interface JnHoldAccountResult {
  readonly accountLast4: string;
  /** The J/N hold codes found on this account, in canonical J-then-N order. */
  readonly holdCodes: readonly string[];
}

/** The J&N-hold verdict + the agent pop-up payload. */
export interface JnHoldResult {
  /** The accounts that carry BOTH a J and an N hold simultaneously. */
  readonly accounts: readonly JnHoldAccountResult[];
  /** The exact fraud-team transfer scripting (customer name interpolated). */
  readonly scripting: string;
}

/**
 * Case-insensitively test whether a set of hold codes carries BOTH a J and an N
 * hold simultaneously.
 */
export function hasJnHold(codes: readonly (string | undefined)[]): boolean {
  const norm = new Set(
    codes
      .map((c) => (c ?? '').trim().toUpperCase())
      .filter((c) => c.length > 0),
  );
  return norm.has('J') && norm.has('N');
}

/**
 * The fraud-team-transfer trigger codes present on an account, in canonical
 * order (J, N, then Q, 8, V): a simultaneous J+N pair (issue #669) and/or any
 * single Q/8/V fraud hold (@dylan219 #784 Option B). Returns [] when the account
 * does not trigger. Case- and whitespace-insensitive. Mirrors the server helper
 * of the same name in `jn-hold-block.ts`.
 */
export function fraudTransferCodes(
  codes: readonly (string | undefined)[],
): string[] {
  const norm = new Set(
    codes
      .map((c) => (c ?? '').trim().toUpperCase())
      .filter((c) => c.length > 0),
  );
  const out: string[] = [];
  if (norm.has('J') && norm.has('N')) out.push('J', 'N');
  for (const code of FRAUD_HOLD_CODES) if (norm.has(code)) out.push(code);
  return out;
}

/**
 * Does a set of hold codes trigger the fraud-team-transfer HARD STOP — a
 * simultaneous J+N hold OR any Q/8/V fraud hold (@dylan219 #784 Option B)?
 */
export function hasFraudTransferHold(
  codes: readonly (string | undefined)[],
): boolean {
  return fraudTransferCodes(codes).length > 0;
}

/**
 * Build the exact fraud-team transfer scripting (issue #669, verbatim), with
 * the customer's display name interpolated. Falls back to a neutral
 * "Thanks," when no name is available so the sentence still reads.
 */
export function jnHoldScripting(customerName: string | undefined): string {
  const name = (customerName ?? '').trim();
  const greeting = name ? `Thanks ${name},` : 'Thanks,';
  return (
    `${greeting} due to the status of your account, I will need to transfer ` +
    `you to our fraud team to discuss your account`
  );
}

/**
 * Normalise + order the codes an account DISPLAYS inside the fraud-team-transfer
 * warning (issue #806): the fraud-trigger codes lead in canonical order (J, N
 * then Q, 8, V), then the ordinary AML codes H, I, O, then any remaining codes
 * in their original order. Case- and whitespace-insensitive; blanks dropped,
 * de-duplicated. Triggering and DISPLAYING are deliberately distinct: only
 * `fraudTransferCodes` TRIGGER the hard stop; this only decides what the warning
 * renders once triggered (@dylan219 #806: "H,I,O hold information was not
 * displayed" — the AML holds of a sibling account must appear too). Mirrors the
 * server twin `holdDisplayCodes` in `server/src/jn-hold-block.ts`.
 */
export function holdDisplayCodes(
  codes: readonly (string | undefined)[],
): string[] {
  const seen = new Set<string>();
  const norm: string[] = [];
  for (const c of codes) {
    const t = (c ?? '').trim().toUpperCase();
    if (t && !seen.has(t)) {
      seen.add(t);
      norm.push(t);
    }
  }
  const out: string[] = [];
  // 1. Fraud-transfer trigger codes present, canonical order.
  for (const c of fraudTransferCodes(norm)) {
    if (!out.includes(c)) out.push(c);
  }
  // 2. Ordinary AML codes, canonical order (#806 display widening).
  for (const c of ORDINARY_AML_HOLD_CODES) {
    if (seen.has(c) && !out.includes(c)) out.push(c);
  }
  // 3. Any remaining codes the account carries, original order.
  for (const c of norm) if (!out.includes(c)) out.push(c);
  return out;
}

/**
 * Evaluate the J&N-hold check over a customer's accounts. The warning TRIGGERS
 * only when AT LEAST ONE account carries a fraud-team-transfer hold (both a J
 * and an N hold simultaneously, or any single Q/8/V fraud hold, @dylan219 #784
 * Option B); `null` otherwise (a lone J, a lone N, or neither does not trigger).
 *
 * Issue #806 (@dylan219 2026-09-16): once TRIGGERED, the warning lists EVERY
 * account of the customer that carries ANY holds — including a sibling account
 * whose holds are only the ordinary AML codes H/I/O ("H,I,O hold information was
 * not displayed"). Each row shows the HOLDING account's own last-4 + all its
 * hold codes in canonical display order. An ordinary-HOLD-ONLY customer never
 * triggers the warning at all (the TRIGGER set is unchanged — H/I/O keep their
 * dismissible Acknowledge pop-up, #784). Pure — see the file header.
 */
export function evaluateJnHold(
  accounts: readonly JnHoldAccountInput[],
  customerName: string | undefined,
): JnHoldResult | null {
  // TRIGGER (unchanged, #669/#784): at least one account must carry a
  // fraud-team-transfer hold.
  if (!accounts.some((a) => hasFraudTransferHold(a.holdCodes))) return null;

  // DISPLAY (widened, #806): every account with holds — fraud OR ordinary —
  // each identified by ITS OWN last 4.
  const matched = accounts
    .map<JnHoldAccountResult>((a) => ({
      accountLast4: a.accountNumber.slice(-4),
      // ALL the codes on this account (fraud triggers + AML H/I/O + any
      // operational codes), canonical display order.
      holdCodes: holdDisplayCodes(a.holdCodes),
    }))
    .filter((a) => a.holdCodes.length > 0 && a.accountLast4.length > 0);

  if (matched.length === 0) return null;

  return {
    accounts: matched,
    scripting: jnHoldScripting(customerName),
  };
}
